import api from './api';
import { env } from '../utils/env';

export interface AtendimentoConexao {
  id: number;
  token: string;
  nome: string;
  descricao?: string;
  tipo: 'whatsapp' | 'instagram';
  tipo_adapter: 'official_api' | 'whaileys' | 'baileys';
  status: 'conectado' | 'desconectado' | 'conectando' | 'aguardando_qr';
  numero?: string;
  ativo: boolean;
  bot_ativo?: boolean;
  flow_id?: number | null;
  flow?: { id: number; name?: string; nome?: string } | null;
  recuse_call: boolean;
  recuse_call_msg?: string | null;
  msgs_boas_vindas: boolean;
  texto_boas_vindas?: string | null;
  msgs_despedida: boolean;
  texto_despedida?: string | null;
  expediente_ativo?: boolean;
  expediente_horarios?: any;
  texto_fora_expediente?: string | null;
  expediente_ignorar_saudacao?: boolean;
  expediente_quando_enviar?: 'novo_ticket' | 'nova_mensagem';
  setor_id?: number | null;
  qrcode?: string | null;
  bateria?: number | null;
  ultima_atividade?: string | null;
  msgs_enviadas: number;
  msgs_recebidas: number;
  createdAt: string;
}

export interface AtendimentoConfiguracao {
  id?: number;
  expediente_ativo: boolean;
  expediente_horarios?: any;
  texto_fora_expediente?: string | null;
  expediente_ignorar_saudacao?: boolean;
  expediente_quando_enviar?: 'novo_ticket' | 'nova_mensagem';
  enviar_msg_transferencia: boolean;
  texto_msg_transferencia?: string | null;
  enviar_msg_assumir_ticket: boolean;
  texto_msg_assumir_ticket?: string | null;
  texto_msg_sigame?: string | null;
  enviar_audio_como_arquivo: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AtendimentoFlowResumo {
  id: number;
  name: string;
  active: boolean;
  description?: string | null;
}

export interface AtendimentoRankingContato {
  id: number;
  nome_contato: string;
  cliente_id: number | null;
  nome_cliente: string | null;
  numero: string | null;
  ddi: string | null;
  atendimentos: number;
}

export interface AtendimentoContato {
  id: number;
  nome: string;
  ddi: string;
  numero: string;
  remote_jid: string;
  canal: 'whatsapp' | 'instagram';
  foto_perfil?: string | null;
  cliente_id?: number | null;
  is_grupo: boolean;
  bot_desativado: boolean;
  tags?: Array<{ id: number; titulo: string; color: string }> | null;
  cliente?: {
    id: number;
    nome: string;
    razao_social?: string;
    status?: 'ativo' | 'inativo' | 'suspenso' | 'inadimplente' | 'bloqueado';
  };
}

export interface AtendimentoTicket {
  id: number;
  status: 'pendente' | 'em_atendimento' | 'fechado' | 'fechado_bot';
  contato_id: number;
  conexao_id: number;
  cliente_id?: number | null;
  usuario_id?: number | null;
  setor_id?: number | null;
  ultima_mensagem?: string | null;
  ultima_msg_at?: string | null;
  msgs_nao_lidas: number;
  is_bot: boolean;
  is_grupo: boolean;
  data_aceite?: string | null;
  data_fechamento?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: any[] | null;
  contato: AtendimentoContato;
  conexao: { id: number; nome: string; tipo: string; status?: string; numero?: string | null };
  usuario?: { id: number; nome: string };
  setor?: { id: number; nome: string; cor?: string | null };
}

export interface AtendimentoMensagem {
  id: number;
  wid: string;
  ticket_id: number;
  contato_id: number;
  conexao_id: number;
  usuario_id?: number | null;
  usuario?: { id: number; nome: string; nome_resumido?: string | null };
  tipo: 'text' | 'private' | 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'location' | 'contact' | 'reaction' | 'pix' | 'expediente';
  corpo?: string | null;
  media_url?: string | null;
  media_tipo?: string | null;
  media_nome?: string | null;
  media_tamanho?: number | null;
  dados_json?: any;
  deletada?: boolean;
  editada?: boolean;
  corpo_editado?: string | null;
  citacao_id?: number | null;
  de_mim: boolean;
  ack: 'pending' | 'sent' | 'received' | 'read' | 'played' | 'error';
  timestamp: string;
}

export interface AtendimentoTicketsFilters {
  status?: string;
  contato_id?: number | null;
  conexao_id?: number | null;
  cliente_id?: number | null;
  usuario_id?: number | null;
  tag_id?: number | string;
  data_criacao_inicio?: string;
  data_criacao_fim?: string;
  data_fechamento_inicio?: string;
  data_fechamento_fim?: string;
}

export interface AtendimentoDashboardData {
  stats: {
    total_atendendo: number;
    total_aguardando: number;
    total_bot: number;
    total_finalizados_hoje: number;
    tma_minutos: number;
    satisfacao_media: number;
    atendentes_online: number;
  };
  atendimentos_recentes: Array<{
    id: number;
    protocolo: string;
    nome_contato: string;
    numero_whatsapp: string;
    status: 'atendendo' | 'aguardando' | 'bot' | 'finalizado';
    mensagens_nao_lidas: number;
    ultima_mensagem?: string;
    ultima_mensagem_at?: string;
    createdAt: string;
    updatedAt: string;
    avatar_cor?: string;
    setor?: string;
    atendente?: { id: number; nome: string; online: boolean };
  }>;
  atendentes: Array<{
    id: number;
    nome: string;
    online: boolean;
    setor?: string;
  }>;
}

export const atendimentoService = {
  getAvatarUrl(foto_perfil?: string | null) {
    if (!foto_perfil) return undefined;
    if (foto_perfil.startsWith('http')) return foto_perfil;
    const baseUrl = env.API_URL.replace(/\/$/, '');
    return `${baseUrl}/uploads/${foto_perfil}`;
  },

  // ─── Conexões ─────────────────────────────────────────────────────────────

  async getConfiguracao() {
    const data: any = await api.get<AtendimentoConfiguracao>('/atendimento/configuracoes');
    return data as AtendimentoConfiguracao;
  },

  async updateConfiguracao(payload: Partial<AtendimentoConfiguracao>) {
    const data: any = await api.put<AtendimentoConfiguracao>('/atendimento/configuracoes', payload);
    return data as AtendimentoConfiguracao;
  },

  async getConexoes() {
    const data: any = await api.get<AtendimentoConexao[]>('/atendimento/conexoes');
    return data;
  },

  async getFluxos() {
    const data: any = await api.get<{ flows: AtendimentoFlowResumo[] }>('/atendimento/fluxos');
    return (data?.flows || []) as AtendimentoFlowResumo[];
  },

  async createConexao(payload: Partial<AtendimentoConexao>) {
    const data: any = await api.post<AtendimentoConexao>('/atendimento/conexoes', payload);
    return data;
  },

  async updateConexao(id: number, payload: Partial<AtendimentoConexao>) {
    const data: any = await api.put<AtendimentoConexao>(`/atendimento/conexoes/${id}`, payload);
    return data;
  },

  async deleteConexao(id: number) {
    const data: any = await api.delete(`/atendimento/conexoes/${id}`);
    return data;
  },

  async startSession(id: number) {
    const data: any = await api.post(`/atendimento/conexoes/${id}/start`);
    return data;
  },

  async stopSession(id: number) {
    const data: any = await api.post(`/atendimento/conexoes/${id}/stop`);
    return data;
  },

  // ─── Tickets ──────────────────────────────────────────────────────────────

  async verificarHistoricoAnterior(ticketId: number) {
    const data: any = await api.get<{ possui: boolean }>(`/atendimento/tickets/${ticketId}/historico-anterior?verificar=1`);
    return Boolean(data?.possui);
  },

  async getHistoricoAnterior(ticketId: number) {
    const data: any = await api.get<{
      possui: boolean;
      ticket: AtendimentoTicket | null;
      mensagens: AtendimentoMensagem[];
      possui_mais: boolean;
    }>(`/atendimento/tickets/${ticketId}/historico-anterior`);
    return data;
  },

  async getTickets(statusOrFilters?: string | AtendimentoTicketsFilters) {
    let url = '/atendimento/tickets';
    const params = new URLSearchParams();

    if (typeof statusOrFilters === 'string') {
      params.set('status', statusOrFilters);
    } else if (statusOrFilters) {
      Object.entries(statusOrFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }

    const query = params.toString();
    if (query) url += `?${query}`;

    const data: any = await api.get<AtendimentoTicket[]>(url);
    return data;
  },

  async updateTicket(id: number, payload: Partial<AtendimentoTicket>) {
    const data: any = await api.put<AtendimentoTicket>(`/atendimento/tickets/${id}`, payload);
    return data;
  },

  async assumirTicket(id: number, payload?: { setor_id?: number | null; sendAssumeMessage?: boolean }) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/assumir`, payload || {});
    return data;
  },

  async marcarTicketComoLido(id: number) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/read`);
    return data;
  },

  async finalizarTicket(id: number, payload?: { sendFarewellMessage?: boolean }) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/finalizar`, payload || {});
    return data;
  },

  async devolverTicket(id: number) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/devolver`);
    return data;
  },

  async reabrirTicket(id: number) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/reabrir`);
    return data;
  },

  async transferirTicket(id: number, payload: { usuario_id?: number | null; setor_id?: number | null; private_message?: string; send_transfer_message?: boolean }) {
    const data: any = await api.post<AtendimentoTicket>(`/atendimento/tickets/${id}/transferir`, payload);
    return data;
  },

  async deleteTicket(id: number) {
    await api.delete(`/atendimento/tickets/${id}`);
  },

  async iniciarTicket(contato_id: number, conexao_id: number, setor_id?: number | null) {
    const data: any = await api.post<{ reutilizado: boolean; ticket: AtendimentoTicket }>('/atendimento/tickets/iniciar', { contato_id, conexao_id, setor_id });
    return data;
  },

  // ─── Mensagens ────────────────────────────────────────────────────────────

  async getMensagens(ticket_id: number) {
    const data: any = await api.get<AtendimentoMensagem[]>(`/atendimento/mensagens?ticket_id=${ticket_id}`);
    return data;
  },

  async sendMensagem(
    ticket_id: number,
    texto: string,
    tipo: 'text' | 'private' = 'text',
    quoted_wid?: string | null,
    usar_assinatura?: boolean,
  ) {
    const data: any = await api.post<AtendimentoMensagem>('/atendimento/send-mensagem', {
      ticket_id,
      texto,
      tipo,
      quoted_wid,
      usar_assinatura: usar_assinatura ?? false,
    });
    return data;
  },

  async sendMedia(ticket_id: number, file: File, caption?: string, usar_assinatura?: boolean) {
    const formData = new FormData();
    formData.append('ticket_id', String(ticket_id));
    formData.append('file', file);
    if (caption?.trim()) formData.append('caption', caption.trim());
    if (usar_assinatura) formData.append('usar_assinatura', 'true');

    const data: any = await api.post<AtendimentoMensagem>('/atendimento/send-media', formData);
    return data;
  },

  async updateMensagem(id: number, texto: string, usar_assinatura?: boolean) {
    const data: any = await api.put<AtendimentoMensagem>(`/atendimento/mensagens/${id}`, {
      texto,
      usar_assinatura: usar_assinatura ?? false,
    });
    return data;
  },

  async deleteMensagem(id: number) {
    const data: any = await api.delete<AtendimentoMensagem>(`/atendimento/mensagens/${id}`);
    return data;
  },

  // ─── Contatos ─────────────────────────────────────────────────────────────

  async getContatos(search?: string, tag_id?: number | string) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tag_id) params.append('tag_id', String(tag_id));
    const queryString = params.toString();
    const url = queryString ? `/atendimento/contatos?${queryString}` : '/atendimento/contatos';
    const data: any = await api.get<AtendimentoContato[]>(url);
    return data;
  },

  async updateContato(id: number, payload: Partial<AtendimentoContato>) {
    const data: any = await api.put<AtendimentoContato>(`/atendimento/contatos/${id}`, payload);
    return data;
  },

  async deleteContato(id: number) {
    const data: any = await api.delete(`/atendimento/contatos/${id}`);
    return data;
  },

  // ─── Dashboard ────────────────────────────────────────────────────────────

  async getDashboard() {
    const data: any = await api.get<AtendimentoDashboardData>('/atendimento/dashboard');
    return data as AtendimentoDashboardData;
  },

  async getRanking(params?: { data_criacao_inicio?: string; data_criacao_fim?: string; setor_id?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.data_criacao_inicio) searchParams.set('data_criacao_inicio', params.data_criacao_inicio);
    if (params?.data_criacao_fim) searchParams.set('data_criacao_fim', params.data_criacao_fim);
    if (params?.setor_id) searchParams.set('setor_id', String(params.setor_id));
    const query = searchParams.toString();
    const url = query ? `/atendimento/ranking?${query}` : '/atendimento/ranking';
    const data: any = await api.get<AtendimentoRankingContato[]>(url);
    return data as AtendimentoRankingContato[];
  },
};
